import React, { useState, useEffect, useMemo } from 'react';
import ReportLayout, {
  DateRangePicker,
  StatusBadge,
  ProgressBar
} from './components/ReportLayout';

import {
  ConciergeBell,
  Users,
  DollarSign,
  Receipt,
  TrendingUp,
  Trophy,
  Target,
  Building2,
  ChevronDown
} from 'lucide-react';

import useWaiterPerformance from '@/hooks/useWaiterPerformance';
import userService from '@/services/userService';
import { useAuth } from "@/context/AuthContext";
import { P } from "@/config/permissionCodes";
const PERIOD_MAP = {
  today: 'today',
  yesterday: 'yesterday',
  thisWeek: 'this_week',
  thisMonth: 'this_month',
  lastMonth: 'last_month',
};

const DEPARTMENTS = [
  { id: 0, name: 'All Departments' },
  { id: 1, name: 'Floor Service' },
  { id: 2, name: 'Counter Service' },
];

export default function WaiterPerformanceReport() {
  const { hasPermission } = useAuth();

if (!hasPermission(P.REPORTS.OPERATIONS.SCREEN)) {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="text-center">
        <h3 className="text-lg font-semibold text-slate-900 mb-2">
          Access Denied
        </h3>
        <p className="text-sm text-slate-500">
          You don't have permission to view this page.
        </p>
      </div>
    </div>
  );
}

  const {
    data,
    summary,
    branchSummary,
    total,
    loading,
    error,
    filters: hookFilters,
    setFilters,
    fetchWaiterPerformance
  } = useWaiterPerformance({
    period: PERIOD_MAP.today,
    offset: 0,
    limit: 25
  });


  const [selectedBranchId, setSelectedBranchId] = useState(null);

  const [cachedBranchOptions, setCachedBranchOptions] = useState([]);
  useEffect(() => {
    if (branchSummary && branchSummary.length > 1 && !selectedBranchId) {
      setCachedBranchOptions(branchSummary.map(b => ({ id: b.branchId, name: b.branchName || b.branchId })));
    }
  }, [branchSummary, selectedBranchId]);
  const branchOptions = cachedBranchOptions;

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedPreset, setSelectedPreset] = useState('today');
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedWaiter, setSelectedWaiter] = useState('');

  const exportToCsv = () => {
    if (!data?.length) return;

    const headers = columns.map(c => `"${c.label}"`).join(',');
    const rows = data.map(r =>
      columns.map(c => `"${r[c.key] ?? ''}"`).join(',')
    );

    const blob = new Blob([headers, '\n', rows.join('\n')], {
      type: 'text/csv;charset=utf-8;',
    });

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'waiter-performance-reports.csv';
    link.click();
  };


  const [sortColumn, setSortColumn] = useState('totalRevenue');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchQuery, setSearchQuery] = useState('');


  const [waiters, setWaiters] = useState([]);

useEffect(() => {
  const loadWaiters = async () => {
    const list = await userService.getWaiters();
    setWaiters(list);
  };

  loadWaiters();
}, []);




  useEffect(() => {
    fetchWaiterPerformance({
      period: PERIOD_MAP.today,
      offset: 0,
      limit: pageSize
    });
  }, []);


 const handleRefresh = () => {

   const payload = {
     startDate,
     endDate,
     waiterId: selectedWaiter,
     department: selectedDepartment,
     offset: (currentPage - 1) * pageSize,
     limit: pageSize,
     sortColumn,
     sortDirection
   };

   if (selectedPreset) {
     payload.period = PERIOD_MAP[selectedPreset];
   }

   Object.keys(payload).forEach(
     k => payload[k] == null || payload[k] === '' ? delete payload[k] : null
   );

   fetchWaiterPerformance(payload);
 };



  const rankedData = useMemo(() => {
    if (!data?.length) return [];

    const maxRevenue = Math.max(...data.map(w => w.totalRevenue || 0), 1);

    return [...data]
      .sort((a, b) => b.totalRevenue - a.totalRevenue)
      .map((w, i) => ({
        ...w,
        rank: i + 1,
        revenuePercentage: ((w.totalRevenue / maxRevenue) * 25)
      }));
  }, [data]);


  const summaryCards = summary ? [
    {
      title: 'Total Staff',
      value: (summary.totalWaiters ?? 0).toLocaleString(),
      subtitle: 'Active waiters',
      icon: Users
    },
    {
      title: 'Total Revenue',
      value: `₹${(summary.totalRevenue ?? 0).toLocaleString()}`,
      subtitle: 'Generated by staff',
      icon: DollarSign
    },
    {
      title: 'Total Bills',
      value: (summary.totalBills ?? 0).toLocaleString(),
      subtitle: 'Bills served',
      icon: Receipt
    },
    {
      title: 'Avg per Waiter',
      value: `₹${(summary.avgRevenuePerWaiter ?? 0).toLocaleString(undefined,{maximumFractionDigits:0})}`,
      subtitle: 'Average revenue',
      icon: TrendingUp
    },
    {
      title: 'Avg Bills/Waiter',
      value: (summary.avgBillsPerWaiter ?? 0).toFixed(0),
      subtitle: 'Bills per person',
      icon: ConciergeBell
    },
    {
      title: 'Top Performer',
      value: summary.topPerformer?.split(' ')[0] ?? '—',
      subtitle: `₹${(summary.topPerformerRevenue ?? 0).toLocaleString()}`,
      icon: Trophy
    }
  ] : [];


  const columns = [
    {
      key: 'rank',
      label: '#',
      sortable: true,
      width: '60px',
      render: (value) => (
        <div className="flex items-center">
          {value <= 3 ? (
            <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
              value === 1 ? 'bg-amber-100 text-amber-600'
              : value === 2 ? 'bg-gray-200 text-gray-500'
              : 'bg-orange-100 text-orange-600'
            }`}>
              <Trophy size={14} />
            </div>
          ) : (
            <span className="w-6 h-6 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center text-xs font-bold">
              {value}
            </span>
          )}
        </div>
      )
    },
    {
      key: 'waiterName',
      label: 'Staff Name',
      sortable: true,
      render: (v, r) => (
        <div>
          <p className="font-medium text-gray-900">{v}</p>
          <StatusBadge
            status={r.department}
            type={r.department === 'Floor Service' ? 'info' : 'default'}
          />
        </div>
      )
    },
    { key: 'workingDays', label: 'Days', sortable: true, align: 'center' },
    { key: 'billsServed', label: 'Bills', sortable: true, align: 'right' },
    {
      key: 'totalRevenue',
      label: 'Revenue',
      sortable: true,
      align: 'right',
      render: v => <span className="font-semibold text-teal-700">₹{v?.toLocaleString()}</span>
    },
    {
      key: 'avgBillValue',
      label: 'Avg Bill',
      sortable: true,
      align: 'right',
      render: v => `₹${v?.toLocaleString(undefined,{maximumFractionDigits:0})}`
    },
    {
      key: 'avgBillsPerDay',
      label: 'Bills/Day',
      sortable: true,
      align: 'right',
      render: v => v?.toFixed(1)
    },
    {
      key: 'revenuePercentage',
      label: 'Contribution',
      sortable: true,
      width: '140px',
      render: v => (
        <div className="w-28">
          <ProgressBar value={v} max={25} color="teal" />
        </div>
      )
    }
  ];


  const pagination = {
    currentPage,
    totalPages: Math.ceil(total / pageSize),
    totalItems: total,
    pageSize,
    onPageChange: (p) => { setCurrentPage(p); handleRefresh(); },
    onPageSizeChange: (s) => { setPageSize(s); setCurrentPage(1); handleRefresh(); }
  };


const filters = (
  <div className="space-y-4">

    <DateRangePicker
      startDate={startDate}
      endDate={endDate}
      selectedPreset={selectedPreset}
      onStartDateChange={(d) => {
        setStartDate(d);
        setSelectedPreset(null); 
      }}
      onEndDateChange={(d) => {
        setEndDate(d);
        setSelectedPreset(null);
      }}
      onPresetSelect={(p) => {
        setSelectedPreset(p);
        setStartDate(null); 
        setEndDate(null);   
        fetchWaiterPerformance({
          period: PERIOD_MAP[p],
          offset: 0,
          limit: pageSize
        });
      }}
    />


    <div className="flex flex-wrap items-center gap-4 pt-4 border-t border-gray-200">

      {/* DEPARTMENT */}
      {/* <div className="flex flex-col gap-1">
        <label className="text-xs font-medium text-gray-600">Department</label>
        <select
          value={selectedDepartment}
          onChange={(e) => setSelectedDepartment(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-300 rounded-lg min-w-[180px]"
        >
          {DEPARTMENTS.map(d => (
            <option key={d.id} value={d.id === 0 ? '' : d.name}>
              {d.name}
            </option>
          ))}
        </select>
      </div> */}

{/* WAITER */}
<div className="flex flex-col gap-1">
  <label className="text-xs font-medium text-gray-600">Waiter</label>
  <select
    value={selectedWaiter}
    onChange={(e) => setSelectedWaiter(e.target.value)}
    className="px-3 py-2 text-sm border border-gray-300 rounded-lg min-w-[180px]"
  >
    <option value="">All Waiters</option>

    {waiters.map(w => (
      <option key={w.id} value={w.id}>
        {w.firstName} {w.lastName}
      </option>
    ))}
  </select>
</div>



      <div className="flex flex-col gap-1">
        <label className="text-xs font-medium text-transparent">Apply</label>
        <button
          onClick={handleRefresh}
          className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-sm font-medium"
        >
          Apply Filters
        </button>
      </div>

      {branchOptions.length > 0 && (
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-600">Branch</label>
          <div className="relative">
            <Building2 size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <select
              value={selectedBranchId ?? ''}
              onChange={(e) => {
                const val = e.target.value || null;
                setSelectedBranchId(val);
                const newFilters = { ...hookFilters, branchId: val || undefined, offset: 0 };
                setFilters(prev => ({ ...prev, branchId: val || undefined, offset: 0 }));
                fetchWaiterPerformance(newFilters);
              }}
              className="pl-9 pr-8 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500 appearance-none cursor-pointer"
            >
              <option value="">All Branches</option>
              {branchOptions.map(b => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
            <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          </div>
        </div>
      )}

    </div>
  </div>
);

 //SORTING

const sortedData = useMemo(() => {
  if (!rankedData?.length || !sortColumn) return rankedData;

  const sorted = [...rankedData];

  sorted.sort((a, b) => {
    let aVal = a[sortColumn];
    let bVal = b[sortColumn];

    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return sortDirection === 'asc'
        ? aVal - bVal
        : bVal - aVal;
    }

    return sortDirection === 'asc'
      ? String(aVal ?? '').localeCompare(String(bVal ?? ''))
      : String(bVal ?? '').localeCompare(String(aVal ?? ''));
  });

  return sorted;
}, [rankedData, sortColumn, sortDirection]);

// CLIENT-SIDE FILTERING - Search across all columns
const filteredData = useMemo(() => {
  if (!searchQuery.trim() || !sortedData) return sortedData;
  const query = searchQuery.toLowerCase().trim();
  return sortedData.filter(row => {
    // Search in waiter name
    if (String(row.waiterName || '').toLowerCase().includes(query)) return true;
    // Search in department
    if (String(row.department || '').toLowerCase().includes(query)) return true;
    // Search in numeric fields
    const numericFields = ['rank', 'workingDays', 'billsServed', 'totalRevenue', 'avgBillValue', 'avgBillsPerDay'];
    for (const field of numericFields) {
      if (row[field] !== undefined && row[field] !== null) {
        if (String(row[field]).includes(query) || Number(row[field]).toLocaleString().includes(query)) return true;
      }
    }
    return false;
  });
}, [sortedData, searchQuery]);

return (
  <ReportLayout
    title="Waiter Performance Report"
    description="Track staff productivity for performance reviews and incentive calculations"
    icon={ConciergeBell}
    exportPermission={P.REPORTS.OPERATIONS.WAITER_EXPORT}
    color="purple"
    category="Operations"
    categoryId="operational"
    summaryCards={summaryCards}
    columns={columns}
    data={filteredData}
    filters={filters}
    loading={loading}
    error={error}
    pagination={pagination}
    sortColumn={sortColumn}
    sortDirection={sortDirection}
    onSort={(column) => {
    let nextDirection;

    if (sortColumn === column) {
      nextDirection = sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      nextDirection = 'desc';
    }

    setSortColumn(column);
    setSortDirection(nextDirection);
    }}

    onSearch={setSearchQuery}
    searchPlaceholder="Search by staff name, revenue..."
    onRefresh={handleRefresh}
            onExport={(format) => {
      if (format === 'csv') exportToCsv();
    }}
  >



    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">


      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
            <DollarSign size={16} className="text-green-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Top Revenue</h3>
            <p className="text-xs text-gray-500">Highest sales</p>
          </div>
        </div>

        <div className="space-y-2">
          {[...rankedData].slice(0, 3).map((waiter, index) => (
            <div
              key={waiter.waiterId || waiter.id}
              className="flex items-center justify-between p-2 bg-green-50 rounded-lg"
            >
              <div className="flex items-center gap-2">
                <span
                  className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                    index === 0
                      ? 'bg-amber-200 text-amber-700'
                      : 'bg-gray-200 text-gray-600'
                  }`}
                >
                  {index + 1}
                </span>
                <span className="text-sm font-medium text-gray-900">
                  {waiter.waiterName}
                </span>
              </div>

              <span className="text-sm font-semibold text-green-600">
                ₹{waiter.totalRevenue?.toLocaleString()}
              </span>
            </div>
          ))}
        </div>
      </div>


      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
            <Receipt size={16} className="text-blue-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Most Active</h3>
            <p className="text-xs text-gray-500">Highest bill count</p>
          </div>
        </div>

        <div className="space-y-2">
          {[...rankedData]
            .sort((a, b) => b.billsServed - a.billsServed)
            .slice(0, 3)
            .map((waiter, index) => (
              <div
                key={waiter.waiterId || waiter.id}
                className="flex items-center justify-between p-2 bg-blue-50 rounded-lg"
              >
                <div className="flex items-center gap-2">
                  <span
                    className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                      index === 0
                        ? 'bg-amber-200 text-amber-700'
                        : 'bg-gray-200 text-gray-600'
                    }`}
                  >
                    {index + 1}
                  </span>
                  <span className="text-sm font-medium text-gray-900">
                    {waiter.waiterName}
                  </span>
                </div>

                <span className="text-sm font-semibold text-blue-600">
                  {waiter.billsServed} bills
                </span>
              </div>
            ))}
        </div>
      </div>


      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
            <Target size={16} className="text-purple-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Best Upseller</h3>
            <p className="text-xs text-gray-500">Highest avg bill</p>
          </div>
        </div>

        <div className="space-y-2">
          {[...rankedData]
            .sort((a, b) => b.avgBillValue - a.avgBillValue)
            .slice(0, 3)
            .map((waiter, index) => (
              <div
                key={waiter.waiterId || waiter.id}
                className="flex items-center justify-between p-2 bg-purple-50 rounded-lg"
              >
                <div className="flex items-center gap-2">
                  <span
                    className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                      index === 0
                        ? 'bg-amber-200 text-amber-700'
                        : 'bg-gray-200 text-gray-600'
                    }`}
                  >
                    {index + 1}
                  </span>
                  <span className="text-sm font-medium text-gray-900">
                    {waiter.waiterName}
                  </span>
                </div>

                <span className="text-sm font-semibold text-purple-600">
                  ₹{waiter.avgBillValue?.toFixed(0)}
                </span>
              </div>
            ))}
        </div>
      </div>

    </div>
  </ReportLayout>
);

}

